import React from 'react';

/**
 * DynamicFormEngine
 * Renders a set of form fields based on a dynamic JSON schema.
 * 
 * Props:
 * @param {Array} fields - Array of field definitions { key, label, type, options, required, placeholder }
 * @param {Object} data - Current form data { [key]: value }
 * @param {Function} onChange - Callback when a field changes (key, value)
 * @param {Boolean} readOnly - If true, form is view only
 */
const DynamicFormEngine = ({ fields = [], data = {}, onChange, readOnly = false }) => {
  if (!fields || fields.length === 0) {
    return null;
  }

  return (
    <div className="dynamic-form-engine" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
      {fields.map((field) => {
        const { key, label, type, options, required, placeholder } = field;
        const value = data[key] || '';

        return (
          <div key={key} className="form-group" style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '8px', display: 'flex', gap: '4px' }}>
              {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
            </label>
            
            {(type === 'text' || type === 'time' || type === 'date' || type === 'datetime-local' || !type) && (
              <input
                type={type === 'time' ? 'time' : (type === 'date' ? 'date' : (type === 'datetime-local' ? 'datetime-local' : 'text'))}
                className="form-input"
                value={value}
                onChange={(e) => onChange && onChange(key, e.target.value)}
                placeholder={placeholder || ''}
                required={required}
                disabled={readOnly}
              />
            )}

            {type === 'number' && (
              <input
                type="number"
                className="form-input"
                value={value}
                onChange={(e) => onChange && onChange(key, e.target.value)}
                placeholder={placeholder || ''}
                required={required}
                disabled={readOnly}
              />
            )}

            {type === 'select' && (
              <select
                className="form-input"
                value={value}
                onChange={(e) => onChange && onChange(key, e.target.value)}
                required={required}
                disabled={readOnly}
              >
                <option value="">{placeholder || 'Select...'}</option>
                {Array.isArray(options) && options.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            )}

            {type === 'checkbox' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: readOnly ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!value}
                  onChange={(e) => onChange && onChange(key, e.target.checked)}
                  required={required}
                  disabled={readOnly}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }}
                />
                <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{label}</span>
              </label>
            )}

            {type === 'radio' && (
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px' }}>
                {options?.map(opt => (
                  <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name={key}
                      value={opt}
                      checked={data[key] === opt}
                      onChange={e => onChange(key, e.target.value)}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            )}

            {type === 'textarea' && (
              <textarea
                className="form-input"
                value={data[key] || ''}
                onChange={e => onChange(key, e.target.value)}
                placeholder={placeholder || ''}
                required={required}
                rows={3}
              />
            )}

            {type === 'file' && (
              <input
                type="file"
                className="form-input"
                onChange={e => onChange(key, e.target.files[0])}
                required={required}
                accept="image/*,application/pdf"
                style={{ padding: '8px 12px' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DynamicFormEngine;
